// Deliberate duplicate of town-square/src/components/arty/card/useArtyImageCard.ts (ADR 0002). A change here belongs there too.
// Client divergences: the profile slug comes from `useAuth().profile` and the public page is
// built with `buildTownSquareUrl` (which carries the viewer's locale). Art review and mockups
// open in a new tab, not embedded: both need a Clerk session and this app is cross-site to
// them, so a frame would show their sign-in page. Edit is client's own `ArtworkDetailsForm` dialog, the
// thumbnail opens this app's `/art/:id` in a tab (the flyout is a modal over the page), and
// there is no "build from scratch" or Escape swallow: every surface is a Radix layer above the
// flyout's Sheet, so Radix gives Escape to the top one.
import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { getSafeImageSrc } from '@arthelper/shared/link-utils';
import { isNonArtworkContext } from '@arthelper/shared/image-context';
import { notify, useArtyChatActions, usePins } from '~/components/arty/actions/ArtyActionsProvider';
import type { ArtworkSummary } from '~/components/arty/ArtyActionsModal';
import { useAnalytics } from '~/context/analytics';
import { useAuth } from '~/context/auth';
import { useImageVisibility } from '~/hooks/useImageVisibility';
import {
  artbookUrl,
  isProcessed,
  isUnavailableSpark,
  mockupsUrl,
  pinnedActions,
  resolveActions,
  reviewUrl,
  shareUrl,
  type ArtyAction,
  type CommunityKind,
  type Translate,
} from '~/lib/arty-actions/catalog';
import { MAX_PINS } from '~/lib/arty-actions/pins';
import { ARTY_CARD_IMAGE_QUERY, ARTY_COMPLETION_TYPES_QUERY } from '~/lib/graphql/arty-card';
import { buildTownSquareUrl } from '~/utils/index';

/** Poll cadence and ceiling while the upload finishes processing. */
const POLL_MS = 2500;
const POLL_CEILING_MS = 60_000;

/** Where a tap ran: a card pill, a tile in More, or an image menu item. */
export type ActionSource = 'card' | 'more' | 'menu';

/** Where the artist reached this piece's actions from: the Arty card, or an image's "..." menu. */
export type CardEntry = 'card' | 'menu';

/** What the card should draw before it draws anything else. */
export type CardRender = 'none' | 'bare' | 'card';

/** The one surface open over the card at a time. */
export type Surface = 'more' | 'edit' | 'share' | 'community' | null;

export type ArtyImageCardInput = {
  imageId?: string;
  src?: string;
  alt?: string;
  /** Rides every card event, so menu-started runs are told apart from the chat card's. */
  entry?: CardEntry;
};

function isPlaceholderImageSrc(src: string): boolean {
  return /\/optimized\/placeholder(?:[/?#]|$)/i.test(src) || /\/thumbnail\/placeholder(?:[/?#]|$)/i.test(src);
}

/** A numeric image id from the URL formats the API emits. */
function imageIdFromSrc(src: string): string | null {
  const match = src.match(/\/optimized\/(\d+)/) ?? src.match(/\/thumbnail\/(\d+)/);
  if (match) {
    return match[1];
  }
  const attachment = src.match(/\/(\d+)\/(\d+)(?:[?#]|$)/);
  if (attachment) {
    return attachment[2];
  }
  return null;
}

function resolveImageId(src: string | null, explicit?: string): string | null {
  if (explicit && /^\d+$/.test(explicit)) {
    return explicit;
  }
  if (!src) {
    return null;
  }
  return imageIdFromSrc(src);
}

function cardRender(displaySrc: string | null, imageId: string | null): CardRender {
  if (!imageId && !displaySrc) {
    return 'none';
  }
  if (!imageId) {
    return 'bare';
  }
  return 'card';
}

function openTab(href: string) {
  window.open(href, '_blank', 'noopener,noreferrer');
}

function dimensionsOf(image: any): string | null {
  if (!image?.width || !image?.height) {
    return null;
  }
  const unit = image.dimensionType === 'centimeters' ? 'cm' : 'in';
  return `${image.width} × ${image.height} ${unit}`;
}

export type ArtyImageCard = ReturnType<typeof useArtyImageCard>;

/**
 * Everything the image action card knows and does, with no markup attached:
 * the read and the processing poll, the one dispatcher every tap goes through,
 * the visibility flip, and which surface is open over the card.
 *
 * A surface opened from More reopens More when it closes, so the artist keeps
 * exploring instead of dead-ending in the chat. Sparks and a posted ask do not return.
 */
export function useArtyImageCard({ imageId: imageIdProp, src, alt, entry = 'card' }: ArtyImageCardInput) {
  const { t } = useTranslation('arty-card');
  const { t: tArtwork } = useTranslation('artwork');
  const { amplitude } = useAnalytics();
  const { profile } = useAuth();
  const { isSignedIn, isBusy, sendText } = useArtyChatActions();
  const { pins } = usePins();
  const visibility = useImageVisibility();

  const [surface, setSurface] = useState<Surface>(null);
  const [returnToMore, setReturnToMore] = useState(false);
  const [communityKind, setCommunityKind] = useState<CommunityKind | null>(null);
  const [pollExhausted, setPollExhausted] = useState(false);

  const rawSrc = getSafeImageSrc(src ?? '');
  const safeSrc = rawSrc && !isPlaceholderImageSrc(rawSrc) ? rawSrc : null;
  const imageId = resolveImageId(safeSrc, imageIdProp);

  const {
    data: imageQuery,
    loading: imageLoading,
    startPolling,
    stopPolling,
    refetch,
  } = useQuery(ARTY_CARD_IMAGE_QUERY, {
    variables: { id: imageId },
    skip: !imageId,
    fetchPolicy: 'cache-first',
  });
  const image = imageQuery?.image ?? null;
  const hasImage = Boolean(image);
  const processed = hasImage && isProcessed(image?.status);

  // A fresh upload gets its title and thumbnail asynchronously: poll until it
  // is processed. An id the API has no image for is never polled, and a poll
  // that hits the ceiling becomes a visible "check again" instead of a silent stop.
  useEffect(() => {
    if (!hasImage || processed || pollExhausted) {
      stopPolling();
      return undefined;
    }
    startPolling(POLL_MS);
    const ceiling = window.setTimeout(() => {
      stopPolling();
      setPollExhausted(true);
    }, POLL_CEILING_MS);
    return () => {
      window.clearTimeout(ceiling);
      stopPolling();
    };
  }, [hasImage, processed, pollExhausted, startPolling, stopPolling]);

  // The last in-flight poll can land after the ceiling fired: a finished piece
  // is not still processing.
  useEffect(() => {
    if (!processed || !pollExhausted) {
      return;
    }
    setPollExhausted(false);
  }, [processed, pollExhausted]);

  /** Check once now and poll again from zero. */
  const retry = () => {
    setPollExhausted(false);
    void refetch();
  };

  const {
    data: completionTypes,
    loading: sparksLoading,
    error: sparksError,
  } = useQuery(ARTY_COMPLETION_TYPES_QUERY, {
    skip: !isSignedIn,
    fetchPolicy: 'cache-first',
  });
  const sparksReady = Boolean(completionTypes) && !sparksLoading && !sparksError;
  const actions = resolveActions(completionTypes?.completionTypes, t as Translate);
  const shortcuts = pinnedActions(actions, pins).slice(0, MAX_PINS);

  const attachment = isNonArtworkContext(image?.context);
  const isPublic = Boolean(image?.isPublic);
  const onProfile = isPublic && !attachment;
  const displaySrc =
    getSafeImageSrc(image?.optimizedUrl ?? '') ?? getSafeImageSrc(image?.thumbnailUrl ?? '') ?? safeSrc;
  const title: string | null = image?.title || alt || null;
  const profileSlug = profile?.profile?.slug;
  const publicUrl =
    profileSlug && image?.slug && !attachment ? buildTownSquareUrl(`/${profileSlug}/image/${image.slug}`) : null;

  const setPublic = (next: boolean) => {
    if (!imageId) {
      return Promise.resolve(false);
    }
    return visibility.setPublic({ imageId, isPublic: next, title: image?.title });
  };

  const toggleVisibility = () => {
    void setPublic(!isPublic);
  };

  const open = (next: Surface, source: ActionSource) => {
    setReturnToMore(source === 'more');
    setSurface(next);
  };

  /** Close a surface: back to More when it was opened from there. */
  const close = () => {
    setSurface(returnToMore ? 'more' : null);
    setReturnToMore(false);
  };

  /** Close without going back to More. */
  const leave = () => {
    setSurface(null);
    setReturnToMore(false);
  };

  const openMore = () => {
    amplitude.track('arty_card_sheet_opened', { image_id: imageId, entry });
    open('more', 'card');
  };

  /** Share on a private portfolio piece makes it public first: sharing is putting it in the world. */
  const share = async (source: ActionSource) => {
    if (!publicUrl) {
      notify(t('availability.make-public'), 'error');
      return;
    }
    if (!onProfile) {
      const ok = await setPublic(true);
      if (!ok) {
        return;
      }
    }
    open('share', source);
  };

  const spark = (action: ArtyAction) => {
    leave();
    if (isUnavailableSpark(action)) {
      notify(t('toast.not-on-plan', { label: action.label }), 'error');
      return;
    }
    if (!action.message) {
      notify(t('toast.action-failed'), 'error');
      return;
    }
    if (isBusy) {
      notify(t('toast.busy'));
      return;
    }
    // The sentence is fixed per tile, so the piece rides the turn separately.
    void sendText(action.message, {
      sparkId: action.completionTypeId,
      imageId: imageId ?? undefined,
      ...(displaySrc ? { imageUrl: displaySrc } : {}),
      ...(image?.title ? { imageTitle: image.title } : {}),
    });
  };

  /** Every tap goes through here, from the card and from More alike. */
  const run = (action: ArtyAction, source: ActionSource) => {
    amplitude.track('arty_card_action_run', {
      image_id: imageId ?? '',
      action_id: action.id,
      kind: action.kind,
      source,
      entry,
    });
    if (!isSignedIn) {
      notify(t('toast.sign-in'), 'error');
      return;
    }
    if (!imageId || !processed) {
      notify(t('toast.processing'), 'error');
      return;
    }
    if (action.communityKind) {
      setCommunityKind(action.communityKind);
      open('community', source);
      return;
    }
    if (action.id === 'edit') {
      open('edit', source);
      return;
    }
    if (action.id === 'mockups') {
      openTab(mockupsUrl(imageId));
      return;
    }
    if (action.id === 'review') {
      openTab(reviewUrl(imageId));
      return;
    }
    if (action.id === 'share') {
      void share(source);
      return;
    }
    if (action.id === 'artbook') {
      openTab(artbookUrl(imageId));
      return;
    }
    if (action.id === 'view') {
      if (!onProfile || !publicUrl) {
        notify(t('availability.make-public'), 'error');
        return;
      }
      openTab(publicUrl);
      return;
    }
    spark(action);
  };

  const openClientArt = () => {
    if (!imageId) {
      return;
    }
    openTab(`${window.location.origin}/art/${imageId}`);
  };

  const summary: ArtworkSummary = {
    thumb: getSafeImageSrc(image?.thumbnailUrl ?? '') ?? displaySrc,
    title: image?.title ?? null,
    medium: image?.artworkSpecs?.medium ?? null,
    dimensions: dimensionsOf(image),
    description: image?.description ?? null,
    tags: image?.tags ?? [],
    isPublic,
  };

  return {
    render: cardRender(displaySrc, imageId),
    imageId: imageId ?? '',
    image,
    pending: imageLoading && !image,
    processed,
    polling: hasImage && !processed && !pollExhausted,
    pollExhausted: pollExhausted && !processed,
    retry,
    title,
    displaySrc,
    isSignedIn,
    actions,
    sparksReady,
    sparksSettled: !sparksLoading,
    shortcuts,
    summary,
    // The chip is for portfolio pieces only: no flag puts an attachment on a profile.
    showVisibility: isSignedIn && Boolean(image) && !attachment,
    isPublic,
    visibilityPending: imageId ? visibility.isSavingKey(`visibility-${imageId}`) : false,
    toggleVisibility,
    run,
    openMore,
    openClientArt,
    surface,
    close,
    leave,
    communityKind,
    shareUrl: publicUrl ? shareUrl(publicUrl) : '',
    shareTitle: title || tArtwork('image-box.share-fallback'),
    shareQuote: title || tArtwork('image-box.share-fallback'),
    initialArtwork: {
      title: image?.title ?? undefined,
      medium: image?.artworkSpecs?.medium ?? undefined,
      dimensions: dimensionsOf(image) ?? undefined,
      thumb: summary.thumb,
    },
  };
}
